import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restaurant as MealIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  getDemoMeals,
  setDemoMeal,
  removeDemoMeal,
  searchRecipes,
  DemoMeal,
  Recipe,
} from '../adminApi';

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday'];
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'] as const;
const MEAL_TYPE_LABELS: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

const DemoMealsEditor: React.FC = () => {
  const [demoMeals, setDemoMeals] = useState<DemoMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recipe picker dialog state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ day: number; mealType: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Recipe[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchDemoMeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDemoMeals();
      setDemoMeals(result.demoMeals || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load demo meals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDemoMeals();
  }, [fetchDemoMeals]);

  // Search for recipes when query changes
  useEffect(() => {
    if (!pickerOpen || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await searchRecipes({
          query: searchQuery,
          limit: 20,
          hasIngredients: true, // Only show recipes with ingredients (expanded)
        });
        setSearchResults(result.recipes || []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, pickerOpen]);

  const getMealForSlot = (day: number, mealType: string): DemoMeal | null => {
    return demoMeals.find((m) => m.day_of_week === day && m.meal_type === mealType) || null;
  };

  const handleSlotClick = (day: number, mealType: string) => {
    setSelectedSlot({ day, mealType });
    setSearchQuery('');
    setSearchResults([]);
    setPickerOpen(true);
  };

  const handleSelectRecipe = async (recipe: Recipe) => {
    if (!selectedSlot) return;

    try {
      await setDemoMeal(selectedSlot.day, selectedSlot.mealType, recipe.recipe_id);
      setPickerOpen(false);
      setSelectedSlot(null);
      fetchDemoMeals();
    } catch (err: any) {
      alert(err.message || 'Failed to set demo meal');
    }
  };

  const handleRemoveMeal = async (day: number, mealType: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Remove this recipe from the demo?')) return;

    try {
      await removeDemoMeal(day, mealType);
      fetchDemoMeals();
    } catch (err: any) {
      alert(err.message || 'Failed to remove demo meal');
    }
  };

  const formatNutrition = (nutritionInfo: any) => {
    if (!nutritionInfo) return null;
    const chips = [];
    if (nutritionInfo.calories) chips.push(`${nutritionInfo.calories} cal`);
    if (nutritionInfo.protein) chips.push(`${nutritionInfo.protein}g protein`);
    return chips;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Demo Meals
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure the 4-day demo meal plan shown to new users during onboarding.
        Select recipes with good images and nutritional information.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Filled slots: {demoMeals.length} / 12
        </Typography>
      </Paper>

      {/* 4-day grid */}
      <Grid container spacing={3}>
        {DAY_LABELS.map((dayLabel, dayIndex) => (
          <Grid size={{ xs: 12, md: 6, lg: 3 }} key={dayIndex}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {dayLabel}
              </Typography>

              <Stack spacing={2}>
                {MEAL_TYPES.map((mealType) => {
                  const meal = getMealForSlot(dayIndex, mealType);
                  return (
                    <Card
                      key={mealType}
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        '&:hover .remove-btn': { opacity: 1 },
                      }}
                    >
                      <CardActionArea onClick={() => handleSlotClick(dayIndex, mealType)}>
                        {meal ? (
                          <>
                            <CardMedia
                              component="img"
                              height={120}
                              image={meal.image_url || '/placeholder-meal.jpg'}
                              alt={meal.recipe_name}
                              sx={{ objectFit: 'cover' }}
                            />
                            <CardContent sx={{ py: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                              >
                                {MEAL_TYPE_LABELS[mealType]}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" noWrap>
                                {meal.recipe_name}
                              </Typography>
                              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                                {meal.prep_time && (
                                  <Chip
                                    icon={<TimeIcon />}
                                    label={`${meal.prep_time} min`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {formatNutrition(meal.nutrition_info)?.map((label, i) => (
                                  <Chip key={i} label={label} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            </CardContent>
                          </>
                        ) : (
                          <CardContent
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              py: 4,
                              color: 'text.secondary',
                            }}
                          >
                            <AddIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                            <Typography variant="body2" fontWeight="bold">
                              {MEAL_TYPE_LABELS[mealType]}
                            </Typography>
                            <Typography variant="caption">Click to add recipe</Typography>
                          </CardContent>
                        )}
                      </CardActionArea>

                      {meal && (
                        <Tooltip title="Remove recipe">
                          <IconButton
                            className="remove-btn"
                            size="small"
                            onClick={(e) => handleRemoveMeal(dayIndex, mealType, e)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(255,255,255,0.9)',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              '&:hover': { bgcolor: 'error.light', color: 'white' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Recipe Picker Dialog */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Select Recipe for {selectedSlot ? `${DAY_LABELS[selectedSlot.day]} ${MEAL_TYPE_LABELS[selectedSlot.mealType]}` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />

          {searchQuery.trim() && !searchLoading && searchResults.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No recipes found. Try a different search term.
            </Typography>
          )}

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {searchResults.map((recipe) => (
              <ListItem
                key={recipe.recipe_id}
                component="div"
                onClick={() => handleSelectRecipe(recipe)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={recipe.image_url || undefined}
                    sx={{ width: 56, height: 56, mr: 1 }}
                  >
                    <MealIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={recipe.name}
                  secondary={
                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                      {recipe.prep_time && (
                        <Chip
                          icon={<TimeIcon />}
                          label={`${recipe.prep_time} min`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {recipe.image_url && (
                        <Chip label="Has image" size="small" color="success" variant="outlined" />
                      )}
                      {recipe.nutrition_info && (
                        <Chip label="Has nutrition" size="small" color="info" variant="outlined" />
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DemoMealsEditor;
